import React, { useEffect, useState } from "react";
import { Bar } from "react-chartjs-2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChartBar,
  faSpinner,
  faInfoCircle,
  faDownload,
  faExpand,
  faFilter,
  faShip,
  faTruck,
  faPlane,
} from "@fortawesome/free-solid-svg-icons";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
  Title,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
  Title,
  ChartDataLabels
);

const EnhancedReferencesPerEntryPointAndLicenseBarChart = ({
  data,
  isLoading,
  onChartItemClick,
  height = "100%",
}) => {
  // Define a modern color palette for the bars
  const colors = [
    "rgba(54, 162, 235, 0.85)", // Blue
    "rgba(75, 192, 192, 0.85)", // Teal
    "rgba(153, 102, 255, 0.85)", // Purple
    "rgba(255, 159, 64, 0.85)", // Orange
    "rgba(255, 99, 132, 0.85)", // Pink
    "rgba(255, 205, 86, 0.85)", // Yellow
    "rgba(43, 138, 201, 0.85)", // Darker Blue
    "rgba(100, 170, 170, 0.85)", // Darker Teal
    "rgba(123, 82, 205, 0.85)", // Darker Purple
    "rgba(225, 129, 34, 0.85)", // Darker Orange
  ];

  // State for chart data
  const [chartData, setChartData] = useState({
    labels: [],
    datasets: [],
  });

  // State for chart information
  const [chartInfo, setChartInfo] = useState({
    total: 0,
    topCombination: { entryPoint: "", license: "", count: 0 },
    entryPoints: 0,
    licenses: 0,
  });

  // State for expanded chart view
  const [isExpanded, setIsExpanded] = useState(false);

  // Helper function to get transport icon based on entry point
  const getTransportIcon = (entryPoint) => {
    if (!entryPoint) return { icon: faChartBar, color: "text-gray-500" };

    const entryPointNum = parseInt(entryPoint, 10);

    // Sea freight entry points
    if ([160, 510, 430].includes(entryPointNum)) {
      return { icon: faShip, color: "text-blue-600" };
    }

    // Road freight entry points
    if ([240].includes(entryPointNum)) {
      return { icon: faTruck, color: "text-green-600" };
    }

    // Air freight entry points
    if ([520, 521, 640].includes(entryPointNum)) {
      return { icon: faPlane, color: "text-purple-600" };
    }

    // Default for unknown entry points
    return { icon: faChartBar, color: "text-gray-500" };
  };

  // Process chart data when the input data changes
  useEffect(() => {
    if (isLoading || !data || data.length === 0) {
      return;
    }

    const processData = () => {
      // Count references by entry point and license
      const entryPointLicenseCounts = {};
      let topCombination = { entryPoint: "", license: "", count: 0 };

      data.forEach((item) => {
        const entryPoint = item.header.pedimento.pedimento_entry_point;
        const license = item.header.pedimento.pedimento_license;

        if (!entryPoint || !license) return;

        if (!entryPointLicenseCounts[entryPoint]) {
          entryPointLicenseCounts[entryPoint] = {};
        }

        entryPointLicenseCounts[entryPoint][license] =
          (entryPointLicenseCounts[entryPoint][license] || 0) + 1;

        // Track top combination
        if (
          entryPointLicenseCounts[entryPoint][license] > topCombination.count
        ) {
          topCombination = {
            entryPoint,
            license,
            count: entryPointLicenseCounts[entryPoint][license],
          };
        }
      });

      // Get unique entry points and licenses
      const entryPoints = Object.keys(entryPointLicenseCounts).sort();
      const uniqueLicenses = new Set();

      // Collect all unique licenses
      entryPoints.forEach((entryPoint) => {
        Object.keys(entryPointLicenseCounts[entryPoint]).forEach((license) => {
          uniqueLicenses.add(license);
        });
      });

      const licenses = Array.from(uniqueLicenses);

      // Create datasets for each license
      const datasets = licenses.map((license, index) => ({
        label: license,
        data: entryPoints.map(
          (entryPoint) => entryPointLicenseCounts[entryPoint][license] || 0
        ),
        backgroundColor: colors[index % colors.length],
        borderColor: colors[index % colors.length].replace("0.85", "1"),
        borderWidth: 1,
        borderRadius: 4,
        hoverBackgroundColor: colors[index % colors.length].replace(
          "0.85",
          "0.95"
        ),
      }));

      // Calculate total references
      let total = 0;
      entryPoints.forEach((entryPoint) => {
        Object.values(entryPointLicenseCounts[entryPoint]).forEach((count) => {
          total += count;
        });
      });

      setChartData({
        labels: entryPoints,
        datasets: datasets,
      });

      setChartInfo({
        total,
        topCombination,
        entryPoints: entryPoints.length,
        licenses: licenses.length,
      });
    };

    processData();
  }, [data, isLoading]);

  // Chart options configuration
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: "index",
      intersect: false,
    },
    scales: {
      y: {
        stacked: true,
        beginAtZero: true,
        title: {
          display: true,
          text: "Number of References",
          font: {
            size: 12,
            weight: "bold",
          },
        },
        ticks: {
          precision: 0,
        },
        grid: {
          color: "rgba(200, 200, 200, 0.2)",
        },
      },
      x: {
        stacked: true,
        title: {
          display: true,
          text: "Entry Point",
          font: {
            size: 12,
            weight: "bold",
          },
        },
        grid: {
          display: false,
        },
      },
    },
    plugins: {
      legend: {
        position: "top",
        labels: {
          padding: 15,
          usePointStyle: true,
          boxWidth: 10,
          font: {
            size: 11,
          },
        },
      },
      tooltip: {
        backgroundColor: "rgba(255, 255, 255, 0.9)",
        titleColor: "#333",
        bodyColor: "#333",
        bodyFont: {
          size: 12,
        },
        borderColor: "rgba(200, 200, 200, 0.9)",
        borderWidth: 1,
        padding: 10,
        boxPadding: 5,
        usePointStyle: true,
        callbacks: {
          title: (context) => `Entry Point: ${context[0].label}`,
          label: (context) => {
            const license = context.dataset.label;
            const value = context.raw;
            return `License ${license}: ${value} references`;
          },
          footer: (context) => {
            // Calculate total for this entry point
            const entryPoint = context[0].label;
            let total = 0;
            context.forEach((item) => {
              total += item.raw;
            });
            return `Total for Entry Point ${entryPoint}: ${total}`;
          },
        },
      },
      title: {
        display: false,
        text: "References per Entry Point and License",
        font: {
          size: 16,
          weight: "bold",
        },
      },
      datalabels: {
        color: "#fff",
        font: {
          weight: "bold",
          size: 10,
        },
        textStrokeColor: "rgba(0, 0, 0, 0.2)",
        textStrokeWidth: 1,
        formatter: (value) => (value > 0 ? value : ""),
        display: (context) => {
          // Only show data labels for non-zero values that are significant enough
          const value = context.dataset.data[context.dataIndex];
          return value > 2; // Only show for values > 2
        },
      },
    },
    onClick: (_, elements) => {
      if (elements.length > 0 && typeof onChartItemClick === "function") {
        const index = elements[0].index;
        const datasetIndex = elements[0].datasetIndex;
        const entryPoint = chartData.labels[index];
        const license = chartData.datasets[datasetIndex].label;
        const count = chartData.datasets[datasetIndex].data[index];
        onChartItemClick({
          type: "entryPointLicense",
          entryPoint: entryPoint,
          license: license,
          count: count,
        });
      }
    },
  };

  // Render loading spinner when data is loading
  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-full">
        <FontAwesomeIcon
          icon={faSpinner}
          className="animate-spin text-xl text-purple-600 mr-2"
        />
        <span className="text-gray-600">Loading chart data...</span>
      </div>
    );
  }

  // Render "no data" message when there is no data
  if (!data || data.length === 0 || chartData.labels.length === 0) {
    return (
      <div className="flex flex-col justify-center items-center h-full">
        <FontAwesomeIcon
          icon={faChartBar}
          className="text-2xl text-gray-400 mb-2"
        />
        <span className="text-gray-500">No data available for this chart</span>
      </div>
    );
  }

  return (
    <div className="bg-white shadow rounded-lg p-4 w-full h-full flex flex-col">
      {/* Chart Header */}
      <div className="mb-4">
        <h2 className="text-lg font-bold text-gray-800 flex items-center">
          <FontAwesomeIcon icon={faChartBar} className="text-purple-600 mr-2" />
          References per Entry Point and License
        </h2>
      </div>

      {/* Chart Container */}
      <div className="flex-grow relative">
        <Bar data={chartData} options={options} />
      </div>

      {/* Chart Info Footer */}
      <div className="mt-2 pt-2 border-t border-gray-100 flex justify-between items-center text-xs text-gray-500">
        <div>
          <span>
            Total: <strong>{chartInfo.total}</strong>
          </span>
          <span className="mx-2">|</span>
          <span>
            Entry Points: <strong>{chartInfo.entryPoints}</strong>
          </span>
          <span className="mx-2">|</span>
          <span>
            Licenses: <strong>{chartInfo.licenses}</strong>
          </span>
        </div>
        <div className="flex space-x-1">
          {/* These buttons could have actual functionality in a real implementation */}
          <button
            title="Download chart"
            className="p-1 hover:text-purple-600 transition-colors"
          >
            <FontAwesomeIcon icon={faDownload} />
          </button>
          <button
            title="Expand chart"
            className="p-1 hover:text-purple-600 transition-colors"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            <FontAwesomeIcon icon={faExpand} />
          </button>
          <button
            title="Filter data"
            className="p-1 hover:text-purple-600 transition-colors"
          >
            <FontAwesomeIcon icon={faFilter} />
          </button>
        </div>
      </div>

      {/* Expanded View Modal (simplified implementation) */}
      {isExpanded && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
          onClick={() => setIsExpanded(false)}
        >
          <div
            className="bg-white rounded-lg shadow-xl w-4/5 h-4/5 max-w-6xl p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">
                References per Entry Point and License
              </h3>
              <button
                className="text-gray-500 hover:text-gray-700"
                onClick={() => setIsExpanded(false)}
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <div className="h-5/6">
              <Bar data={chartData} options={options} />
            </div>

            {/* Additional Insights in Expanded View */}
            <div className="mt-4 bg-gray-50 p-3 rounded-lg">
              <h4 className="text-sm font-medium text-gray-700 mb-2">
                Key Insights
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white p-3 rounded-lg shadow-sm">
                  <div className="text-xs text-gray-500">Total References</div>
                  <div className="text-xl font-bold text-gray-800">
                    {chartInfo.total}
                  </div>
                </div>
                <div className="bg-white p-3 rounded-lg shadow-sm">
                  <div className="text-xs text-gray-500">
                    Most Common Combination
                  </div>
                  <div className="text-md font-bold text-gray-800">
                    EP: {chartInfo.topCombination.entryPoint} | License:{" "}
                    {chartInfo.topCombination.license}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {chartInfo.topCombination.count} references
                  </div>
                </div>
                <div className="bg-white p-3 rounded-lg shadow-sm">
                  <div className="text-xs text-gray-500">Distribution</div>
                  <div className="text-md font-bold text-gray-800">
                    {chartInfo.entryPoints} Entry Points | {chartInfo.licenses}{" "}
                    Licenses
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnhancedReferencesPerEntryPointAndLicenseBarChart;
